import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import db from '@/db';
import { formatCurrency } from '@/lib/formatters';
import { Badge } from '@/components/ui/badge';
import { MapPin, CheckCircle, Bike, ChefHat, Package, ShieldCheck, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_STEPS = [
  { key: 'preparing', label: 'Preparando', icon: ChefHat },
  { key: 'ready', label: 'Pronto', icon: Package },
  { key: 'delivering', label: 'A caminho', icon: Bike },
  { key: 'delivered', label: 'Entregue', icon: CheckCircle },
];

export default function TrackingPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const mpId = searchParams.get('mp'); // suporte a /rastreio/x?mp=paymentId
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        // 1. Tenta buscar no Dexie local (funciona para admin/staff com dados locais)
        const o = await db.deliveryOrders.get(Number(id));
        if (o) {
          setOrder(o);
          setNotFound(false);
          return;
        }

        // 2. Fallback: busca no backend API (funciona para clientes com Dexie vazio)
        if (API_URL) {
          const lookupId = mpId || id;
          const resp = await fetch(`${API_URL}/api/tracking/${encodeURIComponent(lookupId)}`);
          if (resp.ok) {
            const data = await resp.json();
            if (data.found) {
              setOrder(data);
              setNotFound(false);
              return;
            }
          }
        }

        // 3. Último recurso: pedido mais recente no Dexie local
        const all = await db.deliveryOrders.toArray();
        if (all.length > 0) {
          const latest = all.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
          setOrder(latest);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error loading order:', err);
      }
    };
    load();
    const poll = setInterval(load, 5000);
    return () => clearInterval(poll);
  }, [id, mpId]);

  // Initialize map
  useEffect(() => {
    if (!order || mapInstanceRef.current) return;
    const initMap = async () => {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');

      if (!mapRef.current || mapInstanceRef.current) return;

      const restLat = -22.6128;
      const restLng = -43.1635;
      const custLat = order.customerLat || -22.6128;
      const custLng = order.customerLng || -43.1635;

      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
        tap: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: 'OpenStreetMap',
      }).addTo(map);

      // Restaurant marker
      const restIcon = L.divIcon({
        html: '<div style="background:#2563eb;color:white;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;border:3px solid white;box-shadow:0 4px 12px rgba(0,32,245,0.4)">K</div>',
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        className: '',
      });
      L.marker([restLat, restLng], { icon: restIcon }).addTo(map).bindPopup('Kaven Local');

      // Customer marker
      const custIcon = L.divIcon({
        html: '<div style="background:#E11D48;color:white;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;border:3px solid white;box-shadow:0 4px 12px rgba(225,29,72,0.4)">&#x1F3E0;</div>',
        iconSize: [30, 30],
        iconAnchor: [15, 15],
        className: '',
      });
      L.marker([custLat, custLng], { icon: custIcon }).addTo(map).bindPopup(order.customerName || 'Cliente');

      // Auto-zoom para cobrir Restaurante + Cliente (mobile-first)
      const bounds = L.latLngBounds([[restLat, restLng], [custLat, custLng]]);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });

      mapInstanceRef.current = map;
    };
    initMap();
  }, [order]);

  // v7.33 — REMOVIDO: `startSimulation()` que automaticamente progredia
  // o status para 'delivering' → 'delivered' a partir da tela do cliente.
  // Agora apenas administradores/entregadores podem mudar status, e
  // 'delivered' exige validação do PIN de 4 dígitos (helper deliveryPin.js).
  // A tela de rastreio é READ-ONLY pro cliente.

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex flex-col items-center justify-center gap-4">
        {notFound ? (
          <>
            <AlertCircle size={40} className="text-zinc-400" />
            <p className="text-zinc-500 font-heading text-xl">Pedido não encontrado</p>
            <p className="text-zinc-400 text-sm text-center max-w-xs">
              O pedido pode ainda estar sendo processado. Tente novamente em alguns instantes.
            </p>
            <button onClick={() => { setNotFound(false); }} className="text-sm font-bold text-[#2563eb] hover:underline">
              Tentar novamente
            </button>
          </>
        ) : (
          <p className="text-zinc-400 animate-pulse font-heading text-xl">Carregando pedido...</p>
        )}
        <a href="/delivery" className="text-sm font-bold text-[#2563eb] hover:underline">Fazer novo pedido</a>
      </div>
    );
  }

  const currentStepIdx = STATUS_STEPS.findIndex((s) => s.key === order.status);

  return (
    <div data-testid="tracking-page" className="min-h-screen bg-[#F4F4F5]">
      {/* Header mobile-first */}
      <div className="bg-white border-b-2 border-zinc-200 px-4 py-3 sticky top-0 z-30">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-lg font-black text-zinc-900 tracking-tight">KAVEN</h1>
            <p className="text-[11px] text-zinc-500 font-mono">Pedido #{order.id}</p>
          </div>
          <Badge data-testid="tracking-status-badge" className={order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : order.status === 'delivering' ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-blue-100 text-blue-700 border-blue-200'}>
            {STATUS_STEPS.find((s) => s.key === order.status)?.label || order.status}
          </Badge>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pb-10">
        {/* Mapa alta fidelidade em topo (mobile-first) */}
        <div className="bg-white border-2 border-zinc-200 rounded-2xl overflow-hidden mt-4 shadow-sm">
          <div ref={mapRef} data-testid="tracking-map" style={{ height: 320, width: '100%' }} />
          {order.status === 'delivered' && (
            <div className="p-3 border-t-2 border-zinc-100 text-center">
              <div className="flex items-center justify-center gap-2 text-emerald-600 font-bold text-sm">
                <CheckCircle size={18} /> Pedido entregue com sucesso!
              </div>
            </div>
          )}
        </div>

        {/* v7.33 — PIN de confirmação de entrega (visível só quando "A caminho") */}
        {order.status === 'delivering' && order.deliveryPin && (
          <div
            data-testid="delivery-pin-display"
            className="mt-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-300 rounded-2xl p-5 text-center shadow-sm"
          >
            <div className="flex items-center justify-center gap-2 text-emerald-700 mb-2">
              <ShieldCheck size={18} strokeWidth={2.5} />
              <p className="text-[11px] uppercase tracking-[0.2em] font-black">Código de entrega</p>
            </div>
            <p
              data-testid="delivery-pin-code"
              className="font-mono text-5xl font-black tracking-[0.4em] text-emerald-800 my-2"
            >
              {order.deliveryPin}
            </p>
            <p className="text-xs text-emerald-900/80 leading-snug">
              Informe este código ao <strong>entregador</strong> quando ele chegar.<br />
              É o único jeito de confirmar que você recebeu o pedido.
            </p>
          </div>
        )}

        {/* Timeline Mobile */}
        <div data-testid="tracking-timeline" className="bg-white border-2 border-zinc-200 rounded-2xl p-4 mt-4">
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isActive = idx <= currentStepIdx;
              const isCurrent = idx === currentStepIdx;
              return (
                <div key={s.key} className="flex flex-col items-center flex-1">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1.5 transition-all ${isCurrent ? 'bg-[#2563eb] text-white scale-110 shadow-lg shadow-blue-200 ring-4 ring-blue-100 animate-pulse' : isActive ? 'bg-emerald-100 text-emerald-600' : 'bg-zinc-100 text-zinc-400'}`}>
                    <Icon size={15} />
                  </div>
                  <span className={`text-[10px] font-bold text-center leading-tight ${isActive ? 'text-zinc-900' : 'text-zinc-400'}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Resumo do Pedido */}
        <div className="bg-white border-2 border-zinc-200 rounded-2xl p-4 mt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">Resumo do Pedido</p>
          {order.items?.map((item, idx) => (
            <div key={idx} className="flex justify-between text-sm py-1">
              <span className="text-zinc-700">{item.quantity}x {item.name}</span>
              <span className="font-mono font-bold text-zinc-900">{formatCurrency(item.total)}</span>
            </div>
          ))}
          <div className="border-t border-zinc-100 mt-2 pt-2 text-sm flex justify-between text-zinc-600"><span>Entrega</span><span className="font-mono">{formatCurrency(order.deliveryFee)}</span></div>
          <div className="flex justify-between font-bold mt-1"><span>Total</span><span className="font-mono text-base text-zinc-900">{formatCurrency(order.total)}</span></div>
          {order.neighborhood && (
            <div className="flex items-center gap-1 text-[11px] text-zinc-500 mt-3 pt-3 border-t border-zinc-100">
              <MapPin size={12} /> {order.neighborhood}{order.address ? ` • ${order.address}` : ''}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
